import { useState, useEffect } from 'react'
import { adminApi } from '../api/admin'
import { getErrorMessage } from '@/shared/api/client'
import { toast } from '@/shared/stores/toastStore'
import { useAuthStore } from '@/features/auth/stores/authStore'
import type { UserResponse, UserPermissionJSON, DeviceResponse } from '@/shared/api/generated'

type Tab = 'active' | 'deleted'
type PermKey = keyof UserPermissionJSON

const PERM_COLUMNS: { key: PermKey; label: string }[] = [
  { key: 'can_mount', label: 'Mount' },
  { key: 'can_create_group', label: 'Groups' },
  { key: 'can_edit_tags', label: 'Tags' },
  { key: 'can_edit_vals', label: 'Values' },
]

export function UserAdmin() {
  const currentUser = useAuthStore((s) => s.user)
  const [users, setUsers] = useState<UserResponse[]>([])
  const [deletedUsers, setDeletedUsers] = useState<UserResponse[]>([])
  const [tab, setTab] = useState<Tab>('active')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [newUsername, setNewUsername] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [resetUserId, setResetUserId] = useState<number | null>(null)
  const [resetPw, setResetPw] = useState('')
  const [devicePopup, setDevicePopup] = useState<{ userId: number; username: string; devices: DeviceResponse[] } | null>(null)
  const [staged, setStaged] = useState<Map<number, Partial<UserPermissionJSON>>>(new Map())

  useEffect(() => { loadUsers() }, [])

  const flash = (msg: string) => { setSuccess(msg); setTimeout(() => setSuccess(''), 4000) }
  const hasChanges = staged.size > 0

  const loadUsers = async () => {
    setLoading(true)
    setError('')
    try {
      const [active, deleted] = await Promise.all([
        adminApi.listUsers(),
        adminApi.listDeletedUsers().catch(err => { toast.error(getErrorMessage(err)); return [] as UserResponse[] }),
      ])
      setUsers(active)
      setDeletedUsers(deleted)
      setStaged(new Map())
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const getEffectivePerm = (user: UserResponse, key: PermKey): boolean => {
    const s = staged.get(user.id)
    return s?.[key] ?? user.user_permissions?.[key] ?? false
  }

  const stagePerm = (userId: number, key: PermKey, value: boolean) => {
    setStaged((prev) => {
      const next = new Map(prev)
      const existing = next.get(userId) || {}
      const updated = { ...existing, [key]: value }
      // Remove entry if all staged values match server state
      const user = users.find((u) => u.id === userId)
      if (user) {
        const allMatch = PERM_COLUMNS.every((col) =>
          (updated[col.key] ?? user.user_permissions?.[col.key]) === (user.user_permissions?.[col.key] ?? false)
        )
        if (allMatch) { next.delete(userId); return next }
      }
      next.set(userId, updated)
      return next
    })
  }

  const savePerms = async () => {
    setSaving(true)
    setError('')
    try {
      const promises = Array.from(staged).map(([userId]) => {
        const perms: UserPermissionJSON = {
          can_mount: getEffectivePerm(users.find(u => u.id === userId)!, 'can_mount'),
          can_create_group: getEffectivePerm(users.find(u => u.id === userId)!, 'can_create_group'),
          can_edit_tags: getEffectivePerm(users.find(u => u.id === userId)!, 'can_edit_tags'),
          can_edit_vals: getEffectivePerm(users.find(u => u.id === userId)!, 'can_edit_vals'),
        }
        return adminApi.setUserPermissions(userId, perms)
      })
      await Promise.all(promises)
      flash('Permissions saved')
      await loadUsers()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const handleCreateUser = async () => {
    if (!newUsername || !newPassword) return
    setError('')
    try {
      await adminApi.createUser(newUsername, newPassword)
      setNewUsername('')
      setNewPassword('')
      flash(`User "${newUsername}" created`)
      await loadUsers()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      await adminApi.updatePermission(userId, newRole)
      await loadUsers()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleDisable2FA = async (userId: number) => {
    if (!window.confirm('Disable 2FA for this user? They will need to set it up again.')) return
    try {
      await adminApi.disable2FA(userId)
      flash('2FA disabled')
      await loadUsers()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleResetPassword = async (userId: number) => {
    if (!resetPw || resetPw.length < 8) { setError('Password must be at least 8 characters'); return }
    try {
      await adminApi.resetPassword(userId, resetPw)
      setResetUserId(null)
      setResetPw('')
      flash('Password reset')
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Delete this user? You can restore them later from the Deleted Users tab.')) return
    try {
      await adminApi.deleteUser(userId)
      flash('User deleted')
      await loadUsers()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleRestore = async (userId: number) => {
    try {
      await adminApi.restoreUser(userId)
      flash('User restored')
      await loadUsers()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleShowDevices = async (userId: number, username: string) => {
    try {
      const devices = await adminApi.getUserDevices(userId)
      setDevicePopup({ userId, username, devices })
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleRevokeDevice = async (userId: number, deviceId: string) => {
    if (!window.confirm('Revoke this device?')) return
    try {
      await adminApi.revokeUserDevice(userId, deviceId)
      const devices = await adminApi.getUserDevices(userId)
      setDevicePopup(prev => prev ? { ...prev, devices } : null)
      await loadUsers()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  if (loading) return (
    <div className="page">
      <div className="page-header">
        <h1>User Administration</h1>
        <p className="muted">Manage users and permissions</p>
      </div>
      <p className="muted">Loading...</p>
    </div>
  )

  return (
    <div className="page">
      <div className="page-header">
        <h1>User Administration</h1>
        <p className="muted">Manage users and permissions</p>
      </div>

      {error && (
        <div className="alert error">
          {error}
          <button className="dismiss" onClick={() => setError('')}>✕</button>
        </div>
      )}
      {success && <div className="alert success">{success}</div>}

      <div className="card create-user-form">
        <div className="field">
          <label>Username</label>
          <input type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="username" autoComplete="off" />
        </div>
        <div className="field">
          <label>Password</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="password" autoComplete="new-password" />
        </div>
        <button className="btn-success" onClick={handleCreateUser} disabled={!newUsername || !newPassword}>
          Create User
        </button>
      </div>

      <div className="tabs">
        <button className={tab === 'active' ? 'active' : ''} onClick={() => setTab('active')}>
          Active Users ({users.length})
        </button>
        <button className={tab === 'deleted' ? 'active' : ''} onClick={() => setTab('deleted')}>
          Deleted Users ({deletedUsers.length})
        </button>
      </div>

      {tab === 'active' && (
        <div className="card">
          {hasChanges && (
            <div className="button-row">
              <button className="btn-secondary" onClick={() => setStaged(new Map())} disabled={saving}>Discard</button>
              <button className="btn-primary" onClick={savePerms} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th className="centered">Role</th>
                <th className="centered">2FA</th>
                {PERM_COLUMNS.map((col) => (
                  <th key={col.key} className="centered">{col.label}</th>
                ))}
                <th className="centered">Devices</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isSelf = currentUser?.id === user.id
                const isChanged = staged.has(user.id)
                return (
                  <tr key={user.id} className={isChanged ? 'changed' : undefined}>
                    <td>
                      {user.username}
                      {isSelf && <span className="muted"> (you)</span>}
                    </td>
                    <td className="centered">
                      {isSelf ? (
                        <span className="muted">{roleLabel(user.site_permission)}</span>
                      ) : (
                        <select
                          value={user.site_permission}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          data-testid={`role-${user.id}`}
                        >
                          <option value="user">User</option>
                          <option value="user-admin">Admin</option>
                        </select>
                      )}
                    </td>
                    <td className="centered">
                      {user.totp_enabled ? (
                        <span>
                          ✓
                          {!isSelf && (
                            <button className="link danger" onClick={() => handleDisable2FA(user.id)}>
                              Disable
                            </button>
                          )}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    {PERM_COLUMNS.map((col) => (
                      <td key={col.key} className="centered">
                        <input
                          type="checkbox"
                          checked={getEffectivePerm(user, col.key)}
                          onChange={(e) => stagePerm(user.id, col.key, e.target.checked)}
                          data-testid={`${col.key.replace('can_', '').replace('_', '-')}-${user.id}`}
                        />
                      </td>
                    ))}
                    <td className="centered">
                      {user.device_count > 0 ? (
                        <button className="link" onClick={() => handleShowDevices(user.id, user.username)} data-testid={`devices-${user.id}`}>
                          {user.device_count}
                        </button>
                      ) : (
                        <span className="muted">0</span>
                      )}
                    </td>
                    <td className="right actions">
                      {!isSelf && (
                        <>
                          {resetUserId === user.id ? (
                            <span className="inline-reset">
                              <input
                                type="password"
                                value={resetPw}
                                onChange={e => setResetPw(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleResetPassword(user.id); if (e.key === 'Escape') { setResetUserId(null); setResetPw('') } }}
                                placeholder="new password"
                                autoFocus
                                autoComplete="new-password"
                              />
                              <button className="link success" onClick={() => handleResetPassword(user.id)}>Set</button>
                              <button className="link" onClick={() => { setResetUserId(null); setResetPw('') }}>✕</button>
                            </span>
                          ) : (
                            <button className="link" onClick={() => setResetUserId(user.id)}>Reset PW</button>
                          )}
                          <button className="link danger" onClick={() => handleDelete(user.id)}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'deleted' && (
        <div className="card">
          {deletedUsers.length === 0 ? (
            <div className="empty-state">No deleted users</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th className="centered">Role</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deletedUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="muted">{user.username}</td>
                    <td className="centered muted">{roleLabel(user.site_permission)}</td>
                    <td className="right">
                      <button className="link success" onClick={() => handleRestore(user.id)}>Restore</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {devicePopup && (
        <div className="modal-overlay" onClick={() => setDevicePopup(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Devices — {devicePopup.username}</h3>
              <button className="dismiss" onClick={() => setDevicePopup(null)}>✕</button>
            </div>
            <div className="modal-body">
              {devicePopup.devices.length === 0 ? (
                <p className="muted">No devices</p>
              ) : (
                devicePopup.devices.map(d => (
                  <div key={d.device_id} className="device-row">
                    <div>
                      <div className="device-name">{d.device_name}</div>
                      <div className="muted small">Last used: {d.last_used || 'Never'}</div>
                    </div>
                    <button className="link danger" onClick={() => handleRevokeDevice(devicePopup.userId, d.device_id)} data-testid={`revoke-${d.device_id}`}>
                      Revoke
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function roleLabel(perm: string): string {
  switch (perm) {
    case 'user-admin': return 'Admin'
    default: return 'User'
  }
}
